import React, { useEffect, useState } from 'react';

import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import { JsonFormsCore } from '@jsonforms/core';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CardContent from '@mui/material/CardContent';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import { styled } from '@mui/material/styles';
import { Form } from './forms/form';
import Tooltip from '@mui/material/Tooltip';
import { COLORS, SPACING } from '../../styles/theme';

// Styled components for MUI v5
const StyledFormWrapper = styled('div')(() => ({
  margin: 'auto',
  padding: SPACING.FORM_PADDING,
  overflow: 'inherit',
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  borderBottomColor: theme.palette.divider,
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  padding: SPACING.CARD_HEADER_PADDING,
}));

const StyledCardContent = styled(CardContent)(() => ({
  '& .MuiPaper-elevation1': {
    boxShadow: '0 0 0 0',
  },
}));

const StyledCardMarginButton = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const StyledEditIcon = styled(IconButton)(() => ({
  float: 'right',
}));

const StyledClearIcon = styled(ClearIcon)(() => ({
  color: COLORS.PRIMARY_BLUE,
}));

interface EditFormProps {
  onSave(data: any): void;
  onChange?(state: Pick<JsonFormsCore, 'data' | 'errors'>): void;
  data: any;
  title: string;
  uischema: any;
  schema: any;
  isReadonly: boolean;
  onEdit(state: boolean): void;
  hideEditButton?: boolean;
  ajv?: any;
}

export const FormCard = ({
  onSave,
  title,
  onChange,
  data,
  uischema,
  schema,
  isReadonly,
  onEdit,
  hideEditButton,
  ajv,
}: EditFormProps) => {
  const [props, setProps] = useState<any>(data);
  const [disabled, setDisabled] = useState(false);
  const [readonly, setReadOnly] = useState(isReadonly);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setProps(data);
  }, [data]);

  useEffect(() => {
    setReadOnly(isReadonly);
  }, [isReadonly]);

  const cancelEditHandler = () => {
    setReadOnly(true);
    setProps(data);
    onEdit(false);
  };

  // Handle the form data in the parent component to isolate form concerns.
  const editHandler = () => {
    setReadOnly(false);
    onEdit(true);
    setDisabled(true);
  };

  // On change handler
  const _changeHandler = (state: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    setDisabled(!!state.errors?.length);
    if (state.errors && state.errors.length) {
      setIsError(true);
    } else {
      setIsError(false);
    }
    if (state.data) {
      setProps(state.data);
    }
    if (onChange) {
      onChange(state);
    }
  };
  return (
    <StyledFormWrapper>
      <Card>
        <StyledCardHeader
          title={title}
          action={
            <>
              {!readonly ? (
                <>
                  <div style={{ textAlign: 'right' }}>
                    <Tooltip
                      title={disabled && isError ? 'Check For Errors' : ''}
                      arrow
                      placement="top"
                    >
                      <span>
                        <IconButton
                          color="primary"
                          aria-label="create"
                          size="small"
                          onClick={() => {
                            onSave(props);
                          }}
                          disabled={disabled}
                          data-testid={`save-${title}`}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <StyledCardMarginButton
                      color="secondary"
                      size="small"
                      onClick={() => {
                        cancelEditHandler();
                      }}
                      data-testid={`cancel-edit-${title}`}
                    >
                      <StyledClearIcon fontSize="small" />
                    </StyledCardMarginButton>
                  </div>
                </>
              ) : (
                <>
                  {!hideEditButton && (
                    <StyledEditIcon
                      aria-label="edit"
                      data-testid={`edit-${title}`}
                      onClick={() => {
                        editHandler();
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </StyledEditIcon>
                  )}
                </>
              )}
            </>
          }
          disableTypography
        />
        <StyledCardContent>
          <Form
            onChange={_changeHandler}
            data={props}
            uischema={uischema}
            schema={schema}
            isReadonly={readonly}
            ajv={ajv}
          />
        </StyledCardContent>
      </Card>
    </StyledFormWrapper>
  );
};
